import { queryClient } from '@/lib/queryClient'
import { getChatDeletionInfo } from '@/store/chat/utils/chatExpiry'

import type { IChatListItem, IPaginatedChatListResponse } from '../types/chat'

/**
 * Applies `patch` to one chat row across every cached chat list, in both the paginated
 * `items` and the `pinned` section beside it — a pinned chat is only in the latter, so
 * patching `items` alone would leave the sidebar's "Pinned" rows stale. `patch` returns
 * the row unchanged to decline the update, which leaves the cache entry untouched.
 */
export function patchChatRowInListQueryCache(
  chatId: string,

  patch: (chat: IChatListItem) => IChatListItem,
) {
  queryClient.setQueriesData<IPaginatedChatListResponse>(
    { queryKey: ['chats', 'list'] },
    (old) => {
      if (!old) return old
      let changed = false
      const patchRows = (rows?: IChatListItem[]) =>
        (rows ?? []).map((row) => {
          if (row.id !== chatId) return row
          const patched = patch(row)
          if (patched !== row) changed = true
          return patched
        })

      const items = patchRows(old.items)
      const pinned = patchRows(old.pinned)
      return changed ? { ...old, items, pinned } : old
    },
  )
}

/** Keeps sidebar chat rows in sync when title changes outside a list refetch. */
export function patchChatListTitleInQueryCache(chatId: string, title: string) {
  patchChatRowInListQueryCache(chatId, (chat) => ({ ...chat, title }))
}

/**
 * When the user continues an expiring chat, the backend bumps its `updated_at` (keeping it
 * active). Reflect that in the sidebar cache so the row leaves "Expiring soon" immediately,
 * without a full list refetch. Scoped to expiring rows so normal chats keep their ordering.
 */
export function bumpExpiringChatUpdatedAtInQueryCache(
  chatId: string,
  updatedAt: number,
) {
  patchChatRowInListQueryCache(chatId, (chat) =>
    getChatDeletionInfo(chat)?.isExpiringSoon
      ? { ...chat, updated_at: updatedAt }
      : chat,
  )
}
